import React, { useState, useEffect } from 'react';
import './Home.css';
import './ProductsActions.css';
import DateFilter from '../DateFilter';
import apiService from '../../services/api';
import PopupModal from '../PopupModal';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const mockSalesData = [
  { name: 'Apr 01', sales: 420 },
  { name: 'Apr 05', sales: 310 },
  { name: 'Apr 10', sales: 550 },
  { name: 'Apr 15', sales: 290 },
  { name: 'Apr 20', sales: 650 },
  { name: 'Apr 25', sales: 810 },
];

const mockFinancialData = [
  { name: 'Q1', sales: 4000 },
  { name: 'Q2', sales: 3000 },
  { name: 'Q3', sales: 5000 },
  { name: 'Q4', sales: 4500 },
];

const mockPaymentDueData = [
  { name: 'Overdue', amount: 1200 },
  { name: 'Due < 7d', amount: 2500 },
  { name: 'Due > 7d', amount: 800 },
];


const Home = ({ currentUser }) => {
  const [selectedLocationId, setSelectedLocationId] = useState(
    currentUser?.business?.location_id || 'all'
  );
  
  const [locations, setLocations] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(),
    end: new Date()
  });
  
  const [profitLossData, setProfitLossData] = useState(null);
  const [purchaseStats, setPurchaseStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [openActionId, setOpenActionId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const toggleActionMenu = (id) => {
    setOpenActionId(openActionId === id ? null : id);
  };

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await apiService.getLocations();
        setLocations(response.data || response || []);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };
    fetchLocations();
  }, []);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!apiService.isTokenValid()) return;

      try {
        setLoading(true);
        
        const formatForApi = (date) => {
          if (!date) return '';
          const d = new Date(date);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const params = {
          start_date: formatForApi(dateRange.start),
          end_date: formatForApi(dateRange.end),
          location_id: selectedLocationId === 'all' ? null : selectedLocationId
        };

        console.log('📊 DASHBOARD DEBUG - Fetch Params:', params);

        const results = await Promise.allSettled([
          apiService.getProfitLossReport(params),
          apiService.getPurchaseStatistics(params)
        ]);

        if (results[0].status === 'fulfilled') {
          console.log('📈 DASHBOARD DEBUG - Profit/Loss Response:', results[0].value);
          setProfitLossData(results[0].value);
        } else {
          console.error('❌ DASHBOARD DEBUG - Profit/Loss Error:', results[0].reason);
        }

        if (results[1].status === 'fulfilled') {
          console.log('🛒 DASHBOARD DEBUG - Purchase Stats Response:', results[1].value);
          setPurchaseStats(results[1].value);
        } else {
          console.error('❌ DASHBOARD DEBUG - Purchase Stats Error:', results[1].reason);
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [dateRange, selectedLocationId]);

  const getSalesMetrics = () => {
    const pData = profitLossData?.data || profitLossData || {};
    const sData = purchaseStats?.data || purchaseStats || {};

    if (loading) {
      const spinner = <svg className="circular-arrow-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></svg>;
      return [
        { title: 'Total Sales', value: spinner, icon: '💰', color: 'blue' },
        { title: 'Net', value: spinner, icon: '📊', color: 'green' },
        { title: 'Invoice due', value: '$ 0.00', icon: '📄', color: 'orange' },
        { title: 'Total Sell Return', value: '$ 0.00', icon: '↩️', color: 'red' },
        { title: 'Total purchase', value: spinner, icon: '🛒', color: 'purple' },
        { title: 'Purchase due', value: '$ 0.00', icon: '⏰', color: 'yellow' },
        { title: 'Total Purchase Return', value: spinner, icon: '🔄', color: 'pink' },
        { title: 'Expense', value: spinner, icon: '💸', color: 'gray' },
      ];
    }

    return [
      { title: 'Total Sales', value: `$ ${parseFloat(pData.total_sell || pData.total_sales || 0).toFixed(2)}`, icon: '💰', color: 'blue' },
      { title: 'Net', value: `$ ${parseFloat(pData.net_profit || pData.net || 0).toFixed(2)}`, icon: '📊', color: 'green' },
      { title: 'Invoice due', value: `$ ${parseFloat(pData.invoice_due || pData.total_sell_due || 0).toFixed(2)}`, icon: '📄', color: 'orange' },
      { title: 'Total Sell Return', value: `$ ${parseFloat(pData.total_sell_return || 0).toFixed(2)}`, icon: '↩️', color: 'red' },
      { title: 'Total purchase', value: `$ ${parseFloat(sData.total_purchase || 0).toFixed(2)}`, icon: '🛒', color: 'purple' },
      { title: 'Purchase due', value: `$ ${parseFloat(sData.purchase_due || sData.total_purchase_due || 0).toFixed(2)}`, icon: '⏰', color: 'yellow' },
      { title: 'Total Purchase Return', value: `$ ${parseFloat(sData.total_purchase_return || 0).toFixed(2)}`, icon: '🔄', color: 'pink' },
      { title: 'Expense', value: `$ ${parseFloat(pData.total_expense || pData.expense || 0).toFixed(2)}`, icon: '💸', color: 'gray' },
    ];
  };

  const checkPayments = [
    { id: 1, customer: 'Walk-In Customer', invoiceNo: 'rgrdgertger mobile api', checkNumber: '', expiryDate: '', submissionDate: '', status: 'Pending' },
    { id: 2, customer: 'Walk-In Customer', invoiceNo: '6656', checkNumber: '2323', expiryDate: '', submissionDate: '', status: 'Pending' },
    { id: 3, customer: 'Shahabaj', invoiceNo: '965632', checkNumber: '695632', expiryDate: '', submissionDate: '', status: 'Pending' },
    { id: 4, customer: 'Shahabaj', invoiceNo: '965632', checkNumber: '698565', expiryDate: '', submissionDate: '', status: 'Pending' },
    { id: 5, customer: 'Shahabaj', invoiceNo: '965632', checkNumber: '9658654', expiryDate: '', submissionDate: '', status: 'Pending' },
  ];

  const stockAlerts = [
    { product: '20 LBS ICE BAG (TNKXMEBD)', location: 'Lefty\'s Liquor', currentStock: -193.00, unit: 'Single' },
    { product: '7 LBS ICE BAG (850484000108)', location: 'Lefty\'s Liquor', currentStock: -149.00, unit: 'Single' },
    { product: 'TITO\'S 50ML (619947000068)', location: 'Lefty\'s Liquor', currentStock: -144.00, unit: 'Single' },
    { product: 'NEW AMSTERDAM PINK WHITNEY VODKA 50 ML (08567507)', location: 'Lefty\'s Liquor', currentStock: -105.00, unit: 'Single' },
    { product: '99 WATERMELON 50 ML (089000991044)', location: 'Lefty\'s Liquor', currentStock: -100.00, unit: 'Single' },
  ];

  useEffect(() => {
    const popupShown = sessionStorage.getItem('welcomePopupShown');
    if (!popupShown) {
      const timer = setTimeout(() => {
        setShowPopup(true);
        sessionStorage.setItem('welcomePopupShown', 'true');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="etpos-home">
      {/* Welcome Section */}
      <div className="welcome-section">
        <h2>Welcome {currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username : 'User'}, 👋</h2>
        <div className="controls-row">
          <div className="location-selector">
            <label>Select location</label>
            <select value={selectedLocationId} onChange={(e) => setSelectedLocationId(e.target.value)}>
              <option value="all">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>
          <div className="location-selector">
            <label>Select date range</label>
            <DateFilter 
              onDateChange={(range) => setDateRange({ start: range.start, end: range.end })}
              initialStartDate={dateRange.start}
              initialEndDate={dateRange.end}
            />
          </div>
        </div>
      </div>

      {/* Sales Metrics */}
      <div className="metrics-grid">
        {getSalesMetrics().map((metric, index) => (
          <div key={index} className={`metric-card metric-${metric.color}`}>
            <div className="metric-icon">{metric.icon}</div>
            <div className="metric-content">
              <p className="metric-title">{metric.title}</p>
              <h3 className="metric-value">{metric.value}</h3>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-card">
          <h3>Sales Last 30 Days</h3>
          <div className="chart-container" style={{ height: '300px', marginTop: '1rem' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={mockSalesData}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" stroke="#64748b" tick={{ fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis stroke="#64748b" tick={{ fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(val) => `$${val}`} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="sales" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="charts-row">
          <div className="chart-card">
            <h3>Sales Current Financial Year</h3>
            <div className="chart-container" style={{ height: '250px', marginTop: '1rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mockFinancialData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" tick={{ fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis stroke="#64748b" tick={{ fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.05)' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }} />
                  <Bar dataKey="sales" fill="#10b981" radius={[4, 4, 0, 0]} barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="chart-card">
            <h3>Sales Payment Due</h3>
            <div className="chart-container" style={{ height: '250px', marginTop: '1rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mockPaymentDueData} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#64748b" tick={{ fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" stroke="#64748b" tick={{ fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: 'rgba(239, 68, 68, 0.05)' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }} />
                  <Bar dataKey="amount" fill="#ef4444" radius={[0, 4, 4, 0]} barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Tables Section */}
      <div className="tables-section">
        {/* Sales Payment Due Table */}
        <div className="table-card">
          <div className="table-header">
            <h3>Sales Payment Due</h3>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Customer</th><th>Invoice No</th><th>Due Amount</th><th>Action</th></tr>
              </thead>
              <tbody>
                <tr><td colSpan="4" className="no-data">No data available in table</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Purchase Payment Due Table */}
        <div className="table-card">
          <div className="table-header">
            <h3>Purchase Payment Due</h3>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Supplier</th><th>Reference No</th><th>Due Amount</th><th>Action</th></tr>
              </thead>
              <tbody>
                <tr><td colSpan="4" className="no-data">No data available in table</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Check Payments Table */}
      <div className="table-card">
        <div className="table-header">
          <h3>Check Payment Dues</h3>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr><th>Customer</th><th>Invoice No</th><th>Check Number</th><th>Expiry</th><th>Submission</th><th>Status</th><th>Action</th></tr>
            </thead>
            <tbody>
              {checkPayments.map((p, i) => (
                <tr key={i}>
                  <td>{p.customer}</td><td>{p.invoiceNo}</td><td>{p.checkNumber}</td><td>{p.expiryDate}</td><td>{p.submissionDate}</td>
                  <td><span className="status-badge pending">Pending</span></td>
                  <td>
                    <div className="action-dropdown-container">
                      <button className="action-btn-trigger" onClick={() => toggleActionMenu(p.id)}>
                        Action <span className="arrow-down">▼</span>
                      </button>
                      {openActionId === p.id && (
                        <div className="action-dropdown-menu">
                          <button onClick={() => {
                            toggleActionMenu(p.id);
                            alert(`View check payment for invoice: ${p.invoiceNo}`);
                          }}>View</button>
                          <button onClick={() => {
                            toggleActionMenu(p.id);
                            alert(`Edit check payment for invoice: ${p.invoiceNo}`);
                          }}>Edit</button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product Stock Alert */}
      <div className="table-card">
        <div className="table-header">
          <h3>Product Stock Alert</h3>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Product</th><th>Location</th><th>Current stock</th></tr></thead>
            <tbody>
              {stockAlerts.map((alert, i) => (
                <tr key={i}><td>{alert.product}</td><td>{alert.location}</td><td>{alert.currentStock.toFixed(2)} {alert.unit}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PopupModal
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        title="Welcome to ET-POS!"
        message="At the moment, only Product and Sell pages are fully functional. Other sections are still under development and will be available soon. Thank you for your patience."
      />
    </div>
  );
};

export default Home;
